import React from 'react';
import classNames from 'classnames';
import { useDispatch } from 'umi';
import ContactAvatar from '@/components/Contacts/ContactAvatar';
import { ServiceRequestContactModel } from '@/typings/models/Contact';
import { getContactName } from '@/utils/contacts';
import styles from './ServiceRequestContact.module.less';

type ServiceRequestContactProps = {
  contact: ServiceRequestContactModel;
  leadId?: string;
  customerId?: string;
  /**
   * `chip` adds the email or phone; `inline` is just avatar and name, for rows
   * and cards; `compact` is a small secondary line in lists.
   */
  variant?: 'chip' | 'inline' | 'compact';
  /** Called before the contact drawer opens, e.g. to close a popover. */
  onOpen?: () => void;
};

/**
 * The person the appointment is with: they get its emails. Opens in the
 * contact drawer without triggering the row or card around it.
 */
const ServiceRequestContact: React.FC<ServiceRequestContactProps> = ({
  contact,
  leadId,
  customerId,
  variant = 'chip',
  onOpen,
}) => {
  const dispatch = useDispatch();
  const detail = variant === 'chip' ? contact.email || contact.phone : null;
  return (
    <button
      type="button"
      className={classNames(styles.contact, {
        [styles.inline]: variant !== 'chip',
        [styles.compact]: variant === 'compact',
      })}
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.();
        dispatch({ type: 'contacts/openDrawer', payload: { contactId: contact.id, customerId, leadId } });
      }}
    >
      <ContactAvatar contact={contact} size="small" className={variant === 'compact' ? styles.compactAvatar : undefined} />
      <span className={styles.name}>{getContactName(contact)}</span>
      {detail && <span className={styles.detail}>{detail}</span>}
    </button>
  );
};

export default React.memo(ServiceRequestContact);
